/**
 * Annotation Nodes for Workflow Editor
 *
 * Non-functional visual elements for organizing workflow canvas:
 * - RectangleAnnotation: Draw boundaries around node groups with drag-to-resize
 * - LabelAnnotation: Drop text notes anywhere on canvas
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { NodeProps, useReactFlow } from 'reactflow';

// Rectangle Annotation Node with resize handles
export const RectangleAnnotation: React.FC<NodeProps> = ({ id, data, selected }) => {
  const { setNodes, getNodes } = useReactFlow();
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState<string | null>(null);
  const startPosRef = useRef({ x: 0, y: 0, width: 0, height: 0, nodeX: 0, nodeY: 0 });

  const width = data.width || 200;
  const height = data.height || 150;
  const color = data.color || '#6B7280';
  const label = data.label || '';

  const handleResizeStart = useCallback((e: React.MouseEvent, direction: string) => {
    e.stopPropagation();
    e.preventDefault();
    setIsResizing(true);
    setResizeDirection(direction);

    // Get current node position for north/west resize calculations
    const currentNode = getNodes().find(n => n.id === id);
    const nodePosition = currentNode?.position || { x: 0, y: 0 };

    startPosRef.current = {
      x: e.clientX,
      y: e.clientY,
      width,
      height,
      nodeX: nodePosition.x,
      nodeY: nodePosition.y
    };
  }, [width, height, getNodes, id]);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - startPosRef.current.x;
      const deltaY = e.clientY - startPosRef.current.y;

      let newWidth = startPosRef.current.width;
      let newHeight = startPosRef.current.height;
      let newX = startPosRef.current.nodeX;
      let newY = startPosRef.current.nodeY;

      // East: expand right - position unchanged
      if (resizeDirection?.includes('e')) {
        newWidth = Math.max(100, startPosRef.current.width + deltaX);
      }
      // West: expand left - move node left as width increases
      if (resizeDirection?.includes('w')) {
        const proposedWidth = startPosRef.current.width - deltaX;
        newWidth = Math.max(100, proposedWidth);
        // Calculate actual width change (respecting minimum)
        const actualWidthChange = newWidth - startPosRef.current.width;
        newX = startPosRef.current.nodeX - actualWidthChange;
      }
      // South: expand down - position unchanged
      if (resizeDirection?.includes('s')) {
        newHeight = Math.max(80, startPosRef.current.height + deltaY);
      }
      // North: expand up - move node up as height increases
      if (resizeDirection?.includes('n')) {
        const proposedHeight = startPosRef.current.height - deltaY;
        newHeight = Math.max(80, proposedHeight);
        // Calculate actual height change (respecting minimum)
        const actualHeightChange = newHeight - startPosRef.current.height;
        newY = startPosRef.current.nodeY - actualHeightChange;
      }

      setNodes(nds => nds.map(n =>
        n.id === id
          ? {
              ...n,
              position: { x: newX, y: newY },
              data: { ...n.data, width: newWidth, height: newHeight }
            }
          : n
      ));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      setResizeDirection(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, resizeDirection, id, setNodes]);

  return (
    <div
      className="pointer-events-auto relative"
      style={{
        width,
        height,
        border: `2px ${selected ? 'solid' : 'dashed'} ${color}`,
        borderRadius: 8,
        backgroundColor: `${color}10`,
      }}
    >
      {/* Label at top */}
      {label && (
        <div
          className="absolute -top-6 left-2 text-xs font-medium px-2 py-0.5 rounded"
          style={{ backgroundColor: color, color: 'white' }}
        >
          {label}
        </div>
      )}

      {/* Resize handles - only show when selected */}
      {/* nodrag class prevents React Flow from initiating drag when clicking handles */}
      {selected && (
        <>
          {/* Corner handles */}
          <div
            className="nodrag nopan absolute w-3 h-3 bg-white border-2 rounded-sm cursor-nwse-resize"
            style={{ borderColor: color, top: -6, left: -6 }}
            onMouseDown={(e) => handleResizeStart(e, 'nw')}
          />
          <div
            className="nodrag nopan absolute w-3 h-3 bg-white border-2 rounded-sm cursor-nesw-resize"
            style={{ borderColor: color, top: -6, right: -6 }}
            onMouseDown={(e) => handleResizeStart(e, 'ne')}
          />
          <div
            className="nodrag nopan absolute w-3 h-3 bg-white border-2 rounded-sm cursor-nesw-resize"
            style={{ borderColor: color, bottom: -6, left: -6 }}
            onMouseDown={(e) => handleResizeStart(e, 'sw')}
          />
          <div
            className="nodrag nopan absolute w-3 h-3 bg-white border-2 rounded-sm cursor-nwse-resize"
            style={{ borderColor: color, bottom: -6, right: -6 }}
            onMouseDown={(e) => handleResizeStart(e, 'se')}
          />

          {/* Edge handles */}
          <div
            className="nodrag nopan absolute w-3 h-3 bg-white border-2 rounded-sm cursor-ns-resize"
            style={{ borderColor: color, top: -6, left: '50%', transform: 'translateX(-50%)' }}
            onMouseDown={(e) => handleResizeStart(e, 'n')}
          />
          <div
            className="nodrag nopan absolute w-3 h-3 bg-white border-2 rounded-sm cursor-ns-resize"
            style={{ borderColor: color, bottom: -6, left: '50%', transform: 'translateX(-50%)' }}
            onMouseDown={(e) => handleResizeStart(e, 's')}
          />
          <div
            className="nodrag nopan absolute w-3 h-3 bg-white border-2 rounded-sm cursor-ew-resize"
            style={{ borderColor: color, top: '50%', left: -6, transform: 'translateY(-50%)' }}
            onMouseDown={(e) => handleResizeStart(e, 'w')}
          />
          <div
            className="nodrag nopan absolute w-3 h-3 bg-white border-2 rounded-sm cursor-ew-resize"
            style={{ borderColor: color, top: '50%', right: -6, transform: 'translateY(-50%)' }}
            onMouseDown={(e) => handleResizeStart(e, 'e')}
          />
        </>
      )}
    </div>
  );
};

// Label Annotation Node
export const LabelAnnotation: React.FC<NodeProps> = ({ data, selected }) => {
  const text = data.text || 'Note';
  const fontSize = data.fontSize || 14;
  const backgroundColor = data.backgroundColor || '#FEF3C7';
  const textColor = data.textColor || '#92400E';

  return (
    <div
      className={`px-3 py-2 rounded-md shadow-sm pointer-events-auto ${selected ? 'ring-2 ring-blue-500' : ''}`}
      style={{
        backgroundColor,
        color: textColor,
        fontSize,
        maxWidth: 300,
        wordBreak: 'break-word',
        minWidth: 60,
      }}
    >
      {text}
    </div>
  );
};
